import React, { useState, useEffect } from 'react';
import { getPayments, processPayment, refundPayment, getPaymentById } from '../../Services/Operations/ProductAPI';
import '../../Styles/ManagementPages.css';
import { useSelector } from 'react-redux';

const SkeletonStatsCard = () => (
  <div className="stat-card skeleton">
    <div className="skeleton-title"></div>
    <div className="skeleton-value"></div>
    <div className="skeleton-subtitle"></div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="skeleton-row">
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-actions"></div></td>
  </tr>
);

const SkeletonTable = () => (
  <div className="table-container skeleton">
    <table className="data-table">
      <thead>
        <tr>
          <th>PNR No</th>
          <th>Date & Time</th>
          <th>User</th>
          <th>Base Fare</th>
          <th>Platform Fees</th>
          <th>CGST</th>
          <th>SGST</th>
          <th>IGST</th>
          <th>Total</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
          <SkeletonTableRow key={i} />
        ))}
      </tbody>
    </table>
  </div>
);

const SkeletonFilterPanel = () => (
  <div className="filter-panel skeleton">
    <div className="filter-header">
      <div className="skeleton-title"></div>
      <div className="skeleton-close"></div>
    </div>
    <div className="filter-grid">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
        <div key={i} className="filter-group">
          <div className="skeleton-label"></div>
          <div className="skeleton-input"></div>
        </div>
      ))}
    </div>
    <div className="filter-actions">
      <div className="skeleton-button"></div>
      <div className="skeleton-button"></div>
    </div>
  </div>
);

const SkeletonModal = () => (
  <div className="modal-overlay">
    <div className="modal-content1 skeleton-modal">
      <div className="modal-header">
        <div className="skeleton-title"></div>
        <div className="skeleton-close"></div>
      </div>
      <div className="modal-body">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => (
          <div key={i} className="skeleton-detail-row">
            <div className="skeleton-label"></div>
            <div className="skeleton-value"></div>
          </div>
        ))}
      </div>
      <div className="modal-footer">
        <div className="skeleton-button"></div>
      </div>
    </div>
  </div>
);

const PaymentReport = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewingPayment, setViewingPayment] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [refundingPayment, setRefundingPayment] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [error, setError] = useState(null);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const { token } = useSelector(state => state.user);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPayments, setTotalPayments] = useState(0);
  const [pageSize] = useState(10);

  const [filters, setFilters] = useState({
    status: 'All Status',
    method: 'All Methods',
    startDate: '',
    endDate: '',
    pnr: '',
    userName: '',
    userEmail: '',
    userPhone: '',
    bookingId: '',
    minAmount: '',
    maxAmount: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchPayments();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [currentPage, searchTerm, filters]);

  const fetchPayments = async () => {
    try {
      setSearching(true);
      setLoading(true);
      setError(null);
      
      const filterParams = {};
      
      if (searchTerm) {
        filterParams.search = searchTerm;
      }
      
      Object.keys(filters).forEach(key => {
        if (filters[key] && filters[key] !== 'All Status' && filters[key] !== 'All Methods') {
          filterParams[key] = filters[key];
        }
      });

      const params = {
        page: currentPage,
        limit: pageSize,
        ...filterParams
      };

      console.log('Fetching payments with params:', params);

      const response = await getPayments(params, token);
      
      if (response && response.success) {
        console.log('Payments response:', response);
        
        const formattedPayments = response.payments.map(payment => {
          const rawData = payment.rawData || payment;
          
          const pnr = rawData.pnr || payment.pnr || payment.transaction || `TXN${payment.id?.slice(-8) || 'N/A'}`;
          
          const pricing = rawData.pricing || {};
          const baseFare = pricing.totalAmount || pricing.baseAmount || payment.amount || 0;
          const platformFee = pricing.platformFee || 0;
          const cgst = pricing.cgst || 0;
          const sgst = pricing.sgst || 0;
          const igst = pricing.igst || 0;
          const total = pricing.payableAmount || (baseFare + platformFee + cgst + sgst + igst) || payment.amount || 0;
          
          const user = rawData.user || {};
          const userName = user.name || payment.user || 'N/A';
          const userEmail = user.email || payment.userEmail || 'N/A';
          const userPhone = user.phone || payment.userPhone || 'N/A';
          
          const createdAt = rawData.createdAt || payment.date;
          let date = payment.date || 'N/A';
          let time = payment.time || 'N/A';
          
          if (createdAt && !payment.date) {
            const d = new Date(createdAt);
            date = d.toLocaleDateString('en-IN');
            time = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
          }
          
          return {
            id: payment.id,
            pnr: pnr,
            date: date,
            time: time,
            user: userName,
            userEmail: userEmail,
            userPhone: userPhone,
            baseFare: baseFare,
            platformFee: platformFee,
            cgst: cgst,
            sgst: sgst,
            igst: igst,
            total: total,
            method: payment.method || 'Online',
            booking: payment.booking || 'N/A',
            status: payment.status || 'pending',
            paymentStatus: payment.paymentStatus || payment.status || 'pending',
            rawData: payment
          };
        });

        setPayments(formattedPayments);
        setTotalPages(response.totalPages || 1);
        setTotalPayments(response.total || 0);
        
        const hasActiveFilters = 
          filters.status !== 'All Status' || 
          filters.method !== 'All Methods' || 
          searchTerm !== '' || 
          Object.values(filters).some(value => 
            value && value !== '' && value !== 'All Status' && value !== 'All Methods'
          );
        setIsFiltered(hasActiveFilters);
      } else {
        setError('Failed to fetch payments.');
        setPayments([]);
      }
    } catch (err) {
      console.error('Error fetching payments:', err);
      setError('Failed to fetch payments. Please try again.');
      setPayments([]);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const getSafeString = (value) => {
    if (value === null || value === undefined) return '';
    return String(value);
  };

  const getSafeLowerCase = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).toLowerCase();
  };

  const getSafeNumber = (value) => {
    if (value === null || value === undefined) return 0;
    return Number(value);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchPayments();
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchPayments();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      status: 'All Status',
      method: 'All Methods',
      startDate: '',
      endDate: '',
      pnr: '',
      userName: '',
      userEmail: '',
      userPhone: '',
      bookingId: '',
      minAmount: '',
      maxAmount: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
    setIsFiltered(false);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleExportReport = () => {
    if (payments.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = ['PNR No', 'Date', 'Time', 'User', 'User Email', 'User Phone', 'Base Fare', 'Platform Fees', 'CGST', 'SGST', 'IGST', 'Total', 'Status'];
    const csvData = payments.map(payment => [
      payment.pnr || '',
      payment.date || '',
      payment.time || '',
      payment.user || '',
      payment.userEmail || '',
      payment.userPhone || '',
      `₹${getSafeNumber(payment.baseFare)}`,
      `₹${getSafeNumber(payment.platformFee)}`,
      `₹${getSafeNumber(payment.cgst)}`,
      `₹${getSafeNumber(payment.sgst)}`,
      `₹${getSafeNumber(payment.igst)}`,
      `₹${getSafeNumber(payment.total)}`,
      payment.status || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `payments-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    alert('Payment report exported successfully!');
  };

  const handleViewPayment = async (payment) => {
    try {
      setIsFetchingDetails(true);
      const response = await getPaymentById(payment.id, token);
      
      if (response.success) {
        const paymentData = response.data || response.payment;
        setViewingPayment({
          ...payment,
          ...paymentData
        });
      }
    } catch (error) {
      console.error('Error fetching payment details:', error);
      setViewingPayment(payment);
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleProcessPayment = (payment) => {
    setProcessingPayment(payment);
  };

  const handleConfirmProcess = async () => {
    try {
      const response = await processPayment(processingPayment.id, token);
      
      if (response.success) {
        alert('Payment processed successfully!');
        setProcessingPayment(null);
        fetchPayments();
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      alert('Error processing payment: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleRefundPayment = (payment) => {
    setRefundingPayment(payment);
    setRefundAmount(payment.total);
    setRefundReason('');
  };

  const handleConfirmRefund = async () => {
    try {
      const refundData = {
        refundAmount: parseFloat(refundAmount),
        reason: refundReason
      };

      const response = await refundPayment(refundingPayment.id, refundData, token);
      
      if (response.success) {
        alert('Payment refunded successfully!');
        setRefundingPayment(null);
        setRefundAmount('');
        setRefundReason('');
        fetchPayments();
      }
    } catch (error) {
      console.error('Error refunding payment:', error);
      alert('Error refunding payment: ' + (error.response?.data?.message || error.message));
    }
  };

  const closeViewModal = () => {
    setViewingPayment(null);
  };

  const closeProcessModal = () => {
    setProcessingPayment(null);
  };

  const closeRefundModal = () => {
    setRefundingPayment(null);
    setRefundAmount('');
    setRefundReason('');
  };

  const handleRefresh = () => {
    setSearchTerm('');
    setCurrentPage(1);
    clearFilters();
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const totalRevenue = payments
    .filter(p => getSafeLowerCase(p.status) === 'confirmed' || getSafeLowerCase(p.paymentStatus) === 'success')
    .reduce((sum, payment) => sum + getSafeNumber(payment.total), 0);

  const successfulPayments = payments.filter(p => 
    getSafeLowerCase(p.status) === 'confirmed' || getSafeLowerCase(p.paymentStatus) === 'success'
  ).length;

  const successRate = totalPayments > 0 ? Math.round((successfulPayments / totalPayments) * 100) : 0;

  const formatCurrency = (amount) => {
    return `₹${getSafeNumber(amount).toLocaleString()}`;
  };

  const getStatusBadgeClass = (status, paymentStatus) => {
    const lowerStatus = getSafeLowerCase(status);
    const lowerPaymentStatus = getSafeLowerCase(paymentStatus);
    
    if (lowerStatus === 'refunded' || lowerStatus === 'cancelled') return 'refunded';
    if (lowerPaymentStatus === 'success' || lowerStatus === 'confirmed') return 'completed';
    if (lowerPaymentStatus === 'pending' || lowerStatus === 'pending') return 'pending';
    if (lowerPaymentStatus === 'failed' || lowerStatus === 'expired') return 'failed';
    return 'pending';
  };

  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let start = Math.max(1, currentPage - 2);
      let end = Math.min(totalPages, start + maxVisiblePages - 1);
      
      if (end - start + 1 < maxVisiblePages) {
        start = Math.max(1, end - maxVisiblePages + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  if (loading && payments.length === 0) {
    return (
      <div className="management-page">
        <div className="page-header">
          <div className="skeleton-header-title"></div>
          <div className="header-actions">
            <div className="skeleton-search"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
          </div>
        </div>

        <div className="stats-cards">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonStatsCard key={i} />
          ))}
        </div>

        <SkeletonTable />

        <div className="pagination-container">
          <div className="skeleton-pagination-info"></div>
          <div className="skeleton-pagination-controls"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Payment Report</h1>
        <div className="header-actions">
          <form onSubmit={handleSearchSubmit} className="search-form">
            <div style={{ position: 'relative' }}>
              <input 
                type="text" 
                placeholder="Search by PNR, user or booking..." 
                className="search-input" 
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ width: "300px" }}
              />
              {searching && (
                <span style={{
                  position: 'absolute', right: '10px', top: '50%',
                  transform: 'translateY(-50%)', fontSize: '12px', color: '#888'
                }}>
                  searching...
                </span>
              )}
            </div>
          </form>
          <button className="filter-btn" onClick={toggleFilters}>
            🔍 Filters
          </button>
          <button className="refresh-btn" onClick={handleRefresh}>
            Refresh
          </button>
          <button className="export-btn" onClick={handleExportReport}>
            Export Report
          </button>
        </div>
      </div>


      {showFilters && !loading && (
        <div className="filter-panel">
          <div className="filter-header">
            <h3>Filter Payments</h3>
            <button className="close-filters" onClick={() => setShowFilters(false)}>
              ×
            </button>
          </div>
          <div className="filter-grid">
            <div className="filter-group">
              <label>PNR No</label>
              <input
                type="text"
                name="pnr"
                value={filters.pnr}
                onChange={handleFilterChange}
                placeholder="Enter PNR number"
              />
            </div>
            <div className="filter-group">
              <label>User Name</label>
              <input
                type="text"
                name="userName"
                value={filters.userName}
                onChange={handleFilterChange}
                placeholder="Enter user name"
              />
            </div>
            <div className="filter-group">
              <label>User Email</label>
              <input
                type="email"
                name="userEmail"
                value={filters.userEmail}
                onChange={handleFilterChange}
                placeholder="Enter user email"
              />
            </div>
            <div className="filter-group">
              <label>User Phone</label>
              <input
                type="text"
                name="userPhone"
                value={filters.userPhone}
                onChange={handleFilterChange}
                placeholder="Enter user phone"
              />
            </div>
            <div className="filter-group">
              <label>Booking ID</label>
              <input
                type="text"
                name="bookingId"
                value={filters.bookingId}
                onChange={handleFilterChange}
                placeholder="Enter booking ID"
              />
            </div>
            <div className="filter-group">
              <label>Payment Status</label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="All Status">All Status</option>
                <option value="Confirmed">Completed</option>
                <option value="Pending">Pending</option>
                <option value="Failed">Failed</option>
                <option value="Expired">Expired</option>
                <option value="Refunded">Refunded</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Payment Method</label>
              <select
                name="method"
                value={filters.method}
                onChange={handleFilterChange}
              >
                <option value="All Methods">All Methods</option>
                <option value="Online">Online</option>
                <option value="Cash">Cash</option>
                <option value="Card">Card</option>
                <option value="UPI">UPI</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Payment From</label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
              />
            </div>
            <div className="filter-group">
              <label>Payment To</label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
              />
            </div>
            <div className="filter-group">
              <label>Min Amount</label>
              <input
                type="number"
                name="minAmount"
                value={filters.minAmount}
                onChange={handleFilterChange}
                placeholder="Minimum amount"
                min="0"
              />
            </div>
            <div className="filter-group">
              <label>Max Amount</label>
              <input
                type="number"
                name="maxAmount"
                value={filters.maxAmount}
                onChange={handleFilterChange}
                placeholder="Maximum amount"
                min="0"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="apply-filters-btn" onClick={applyFilters}>
              Apply Filters
            </button>
            <button className="clear-filters-btn" onClick={clearFilters}>
              Clear All
            </button>
          </div>
          {isFiltered && (
            <div className="active-filters">
              <span>Active Filters:</span>
              {searchTerm && (
                <span className="filter-tag">Search: {searchTerm}</span>
              )}
              {filters.status !== 'All Status' && (
                <span className="filter-tag">Status: {filters.status}</span>
              )}
              {filters.method !== 'All Methods' && (
                <span className="filter-tag">Method: {filters.method}</span>
              )}
              {Object.entries(filters).map(([key, value]) => 
                value && 
                key !== 'status' && 
                key !== 'method' && 
                key !== 'startDate' && 
                key !== 'endDate' && (
                  <span key={key} className="filter-tag">
                    {key}: {value}
                  </span>
                )
              )}
              {filters.startDate && (
                <span className="filter-tag">From: {filters.startDate}</span>
              )}
              {filters.endDate && (
                <span className="filter-tag">To: {filters.endDate}</span>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="stats-cards">
        <div className="stat-card">
          <h3>Total Payments</h3>
          <p>{totalPayments}</p>
          <span>All transactions</span>
        </div>
        <div className="stat-card">
          <h3>Total Revenue</h3>
          <p>{formatCurrency(totalRevenue)}</p>
          <span>successful payments</span>
        </div>
        <div className="stat-card">
          <h3>Success Rate</h3>
          <p>{successRate}%</p>
          <span>{successfulPayments}/{totalPayments} payments</span>
        </div>
        <div className="stat-card">
          <h3>Search Results</h3>
          <p>{payments.length}</p>
          <span>payments found</span>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>PNR No</th>
              <th>Date & Time</th>
              <th>User</th>
              <th>Base Fare</th>
              <th>Platform Fees</th>
              <th>CGST</th>
              <th>SGST</th>
              <th>IGST</th>
              <th>Total</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {payments.length > 0 ? (
              payments.map(payment => (
                <tr key={payment.id}>
                  <td className="transaction">
                    <strong>{getSafeString(payment.pnr)}</strong>
                  </td>
                  <td>
                    <div>
                      <div>{getSafeString(payment.date)}</div>
                      <div className="phone">{getSafeString(payment.time)}</div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div>{getSafeString(payment.user)}</div>
                      <div className="phone">{getSafeString(payment.userPhone)}</div>
                    </div>
                  </td>
                  <td>{formatCurrency(payment.baseFare)}</td>
                  <td>{formatCurrency(payment.platformFee)}</td>
                  <td>{formatCurrency(payment.cgst)}</td>
                  <td>{formatCurrency(payment.sgst)}</td>
                  <td>{formatCurrency(payment.igst)}</td>
                  <td><strong>{formatCurrency(payment.total)}</strong></td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(payment.status, payment.paymentStatus)}`}>
                      {getSafeString(payment.status === 'Confirmed' ? 'Completed' : payment.status)}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="action-btn view" 
                        onClick={() => handleViewPayment(payment)}
                      >
                        Details
                      </button>
                      {(getSafeLowerCase(payment.status) === 'pending' || getSafeLowerCase(payment.paymentStatus) === 'pending') && (
                        <button 
                          className="action-btn process" 
                          onClick={() => handleProcessPayment(payment)}
                        >
                          Process
                        </button>
                      )}
                      {(getSafeLowerCase(payment.status) === 'confirmed' || getSafeLowerCase(payment.paymentStatus) === 'success') && (
                        <button 
                          className="action-btn delete" 
                          onClick={() => handleRefundPayment(payment)}
                        >
                          Refund
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="11" className="no-data">
                  <p>No payments found {isFiltered ? 'matching your filters' : ''}.</p>
                  {isFiltered && (
                    <button className="clear-filters-link" onClick={clearFilters}>
                      Clear filters to see all payments
                    </button>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


      {totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {payments.length} of {totalPayments} payments
            (Page {currentPage} of {totalPages})
            {searchTerm && <span className="filtered-indicator"> • Search: "{searchTerm}"</span>}
            {isFiltered && !searchTerm && <span className="filtered-indicator"> • Filtered Results</span>}
          </div>
          <div className="pagination-controls">
            <button 
              className="pagination-btn" 
              onClick={handlePrevPage}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            
            <div className="page-numbers">
              {generatePageNumbers().map(pageNumber => (
                <button
                  key={pageNumber}
                  className={`page-number ${pageNumber === currentPage ? 'active' : ''}`}
                  onClick={() => handlePageClick(pageNumber)}
                >
                  {pageNumber}
                </button>
              ))}
              
              {totalPages > 5 && currentPage < totalPages - 2 && (
                <>
                  <span className="page-dots">...</span>
                  <button
                    className="page-number"
                    onClick={() => handlePageClick(totalPages)}
                  >
                    {totalPages}
                  </button>
                </>
              )}
            </div>
            
            <button 
              className="pagination-btn" 
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}


      {viewingPayment && !isFetchingDetails && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Payment Details - {getSafeString(viewingPayment.pnr)}</h3>
              <button className="modal-close-btn" onClick={closeViewModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="customer-details">
                <div className="detail-row">
                  <label>PNR No:</label>
                  <span className="transaction"><strong>{getSafeString(viewingPayment.pnr)}</strong></span>
                </div>
                <div className="detail-row">
                  <label>Date:</label>
                  <span>{getSafeString(viewingPayment.date)}</span>
                </div>
                <div className="detail-row">
                  <label>Time:</label>
                  <span>{getSafeString(viewingPayment.time)}</span>
                </div>
                <div className="detail-row">
                  <label>User:</label>
                  <span>{getSafeString(viewingPayment.user)}</span>
                </div>
                <div className="detail-row">
                  <label>Email:</label>
                  <span>{getSafeString(viewingPayment.userEmail)}</span>
                </div>
                <div className="detail-row">
                  <label>Phone:</label>
                  <span>{getSafeString(viewingPayment.userPhone)}</span>
                </div>
                <div className="detail-row">
                  <label>Base Fare:</label>
                  <span>{formatCurrency(viewingPayment.baseFare)}</span>
                </div>
                <div className="detail-row">
                  <label>Platform Fees:</label>
                  <span>{formatCurrency(viewingPayment.platformFee)}</span>
                </div>
                <div className="detail-row">
                  <label>CGST:</label>
                  <span>{formatCurrency(viewingPayment.cgst)}</span>
                </div>
                <div className="detail-row">
                  <label>SGST:</label>
                  <span>{formatCurrency(viewingPayment.sgst)}</span>
                </div>
                <div className="detail-row">
                  <label>IGST:</label>
                  <span>{formatCurrency(viewingPayment.igst)}</span>
                </div>
                <div className="detail-row">
                  <label>Total Amount:</label>
                  <span className="transaction"><strong>{formatCurrency(viewingPayment.total)}</strong></span>
                </div>
                <div className="detail-row">
                  <label>Payment Method:</label>
                  <span className={`payment-method ${getSafeLowerCase(viewingPayment.method).replace(' ', '-')}`}>
                    {getSafeString(viewingPayment.method)}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Booking Reference:</label>
                  <span>{getSafeString(viewingPayment.booking)}</span>
                </div>
                <div className="detail-row">
                  <label>Status:</label>
                  <span className={`status-badge ${getStatusBadgeClass(viewingPayment.status, viewingPayment.paymentStatus)}`}>
                    {getSafeString(viewingPayment.status === 'Confirmed' ? 'Completed' : viewingPayment.status)}
                  </span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-close-btn secondary" onClick={closeViewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {viewingPayment && isFetchingDetails && <SkeletonModal />}


      {processingPayment && (
        <div className="modal-overlay" onClick={closeProcessModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Process Payment</h3>
              <button className="modal-close-btn" onClick={closeProcessModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="customer-details">
                <div className="detail-row">
                  <label>PNR:</label>
                  <span className="transaction"><strong>{getSafeString(processingPayment.pnr)}</strong></span>
                </div>
                <div className="detail-row">
                  <label>User:</label>
                  <span>{getSafeString(processingPayment.user)}</span>
                </div>
                <div className="detail-row">
                  <label>Total Amount:</label>
                  <span><strong>{formatCurrency(processingPayment.total)}</strong></span>
                </div>
                <div className="detail-row">
                  <label>Booking:</label>
                  <span>{getSafeString(processingPayment.booking)}</span>
                </div>
                <div className="detail-row">
                  <label>Current Status:</label>
                  <span className="status-badge pending">Pending</span>
                </div>
                <div className="detail-row">
                  <label>Action:</label>
                  <span>Mark as Completed</span>
                </div>
              </div>
              <div style={{marginTop: '20px', padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px'}}>
                <p style={{margin: '0', color: '#0369a1', fontSize: '14px'}}>
                  <strong>Note:</strong> This will mark the payment as completed and confirm the associated booking.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="action-btn cancel" onClick={closeProcessModal}>
                Cancel
              </button>
              <button className="action-btn process" onClick={handleConfirmProcess}>
                Confirm Process
              </button>
            </div>
          </div>
        </div>
      )}


      {refundingPayment && (
        <div className="modal-overlay" onClick={closeRefundModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Refund Payment</h3>
              <button className="modal-close-btn" onClick={closeRefundModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="customer-details">
                <div className="detail-row">
                  <label>PNR:</label>
                  <span className="transaction"><strong>{getSafeString(refundingPayment.pnr)}</strong></span>
                </div>
                <div className="detail-row">
                  <label>User:</label>
                  <span>{getSafeString(refundingPayment.user)}</span>
                </div>
                <div className="detail-row">
                  <label>Total Amount:</label>
                  <span><strong>{formatCurrency(refundingPayment.total)}</strong></span>
                </div>
                <div className="detail-row">
                  <label>Booking:</label>
                  <span>{getSafeString(refundingPayment.booking)}</span>
                </div>
                
                <div className="detail-row">
                  <label>Refund Amount:</label>
                  <input
                    type="number"
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    placeholder="Enter refund amount"
                    style={{padding: '8px', border: '1px solid #ddd', borderRadius: '4px', width: '200px'}}
                  />
                </div>
                
                <div className="detail-row">
                  <label>Refund Reason:</label>
                  <textarea
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    placeholder="Enter reason for refund"
                    style={{padding: '8px', border: '1px solid #ddd', borderRadius: '4px', width: '200px', minHeight: '60px'}}
                  />
                </div>
              </div>
              <div style={{marginTop: '20px', padding: '15px', backgroundColor: '#fff3cd', borderRadius: '8px'}}>
                <p style={{margin: '0', color: '#856404', fontSize: '14px'}}>
                  <strong>Warning:</strong> This action will cancel the booking and process a refund. This cannot be undone.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="action-btn cancel" onClick={closeRefundModal}>
                Cancel
              </button>
              <button className="action-btn delete" onClick={handleConfirmRefund}>
                Confirm Refund
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentReport;